export interface PasswordStrength {
  score: number;
  level: string;
  color: string;
  bgColor: string;
  suggestions: string[];
}

export const calculatePasswordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return {
      score: 0,
      level: '',
      color: '',
      bgColor: '',
      suggestions: []
    };
  }

  let score = 0;
  const suggestions: string[] = [];

  // Length check
  if (password.length >= 8) {
    score += 1;
  } else if (password.length >= 6) {
    score += 0.5;
    suggestions.push('Use at least 8 characters');
  } else {
    suggestions.push('Use at least 6 characters');
  }

  // Uppercase letters
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    suggestions.push('Add uppercase letters');
  }

  // Lowercase letters
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    suggestions.push('Add lowercase letters');
  }

  // Numbers
  if (/\d/.test(password)) {
    score += 1;
  } else {
    suggestions.push('Add numbers');
  }

  // Special characters
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    score += 1;
  } else {
    suggestions.push('Add special characters (!@#$%^&*)');
  }

  // Check for common weak patterns
  const weakPatterns = [
    /123456/,
    /password/i,
    /qwerty/i,
    /abc123/i,
    /111111/,
    /000000/,
    /(.)\1{2,}/ // repeated characters
  ];

  const hasWeakPattern = weakPatterns.some(pattern => pattern.test(password));
  if (hasWeakPattern) {
    score = Math.max(0, score - 1);
    suggestions.push('Avoid common patterns like "123456" or "password"');
  }

  // Determine level and colors
  let level: string;
  let color: string;
  let bgColor: string;

  if (score >= 4.5) {
    level = 'Very Strong';
    color = 'text-green-400';
    bgColor = 'bg-green-400';
  } else if (score >= 3.5) {
    level = 'Strong';
    color = 'text-green-300';
    bgColor = 'bg-green-300';
  } else if (score >= 2.5) {
    level = 'Medium';
    color = 'text-yellow-400';
    bgColor = 'bg-yellow-400';
  } else if (score >= 1.5) {
    level = 'Weak';
    color = 'text-orange-400';
    bgColor = 'bg-orange-400';
  } else if (score > 0) {
    level = 'Very Weak';
    color = 'text-red-400';
    bgColor = 'bg-red-400';
  } else {
    level = '';
    color = '';
    bgColor = '';
  }

  return {
    score: Math.min(5, Math.max(0, score)),
    level,
    color,
    bgColor,
    suggestions: suggestions.slice(0, 3) // Limit to 3 suggestions
  };
};
